import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  MessageSquare, 
  Printer,
  Sparkles,
  Loader2,
  Copy,
  AlertCircle,
  FileText,
  Search,
  CheckCircle2
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const SBAR_SECTIONS = [
  { 
    key: "situation", 
    label: "S - Situation", 
    icon: AlertCircle,
    color: "bg-red-500/10 border-red-500/30 text-red-400",
    placeholder: "What is happening right now? Be brief and direct.",
    prompts: ["I am calling about...", "The patient is...", "The immediate concern is..."]
  },
  { 
    key: "background", 
    label: "B - Background", 
    icon: FileText,
    color: "bg-blue-500/10 border-blue-500/30 text-blue-400",
    placeholder: "What is the relevant history and context?",
    prompts: ["Diagnosis:", "Relevant history:", "Current treatment:"]
  },
  { 
    key: "assessment", 
    label: "A - Assessment", 
    icon: Search,
    color: "bg-amber-500/10 border-amber-500/30 text-amber-400",
    placeholder: "What do you think is going on? Your clinical judgment.",
    prompts: ["I think the problem is...", "I am concerned about...", "This is (urgent/not urgent) because..."]
  },
  { 
    key: "recommendation", 
    label: "R - Recommendation", 
    icon: CheckCircle2,
    color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
    placeholder: "What do you need or suggest?",
    prompts: ["I recommend...", "I need you to...", "Are there any other orders?"]
  },
];

export const SBARTemplate = ({ onAIAssist }) => {
  const [context, setContext] = useState("");
  const [sections, setSections] = useState({
    situation: "",
    background: "",
    assessment: "",
    recommendation: "",
  });
  const [aiLoading, setAiLoading] = useState(false);

  const updateSection = (key, value) => {
    setSections(prev => ({ ...prev, [key]: value }));
  };

  const getAISuggestions = async () => {
    if (!sections.situation.trim()) {
      toast.error("Please describe the situation first");
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/sbar`, {
        situation: sections.situation.trim(),
        background: sections.background || null,
        assessment: sections.assessment || null,
        recommendation: sections.recommendation || null
      }, { withCredentials: true });

      if (onAIAssist) {
        onAIAssist(response.data.draft);
      }
      toast.success("AI review complete - check the chat");
    } catch (error) {
      toast.error("Failed to get AI review");
    } finally {
      setAiLoading(false);
    }
  };

  const copyToClipboard = () => {
    const text = `SBAR Communication
${context ? `Context: ${context}\n` : ""}
SITUATION:
${sections.situation}

BACKGROUND:
${sections.background}

ASSESSMENT:
${sections.assessment}

RECOMMENDATION:
${sections.recommendation}`;

    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handlePrint = () => window.print();

  return (
    <Card className="bento-card print:shadow-none print:border-gray-300">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-cyan-400" />
            SBAR Communication
          </CardTitle>
          <div className="flex gap-2 no-print">
            <Button variant="outline" size="sm" onClick={copyToClipboard}>
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button size="sm" onClick={getAISuggestions} disabled={aiLoading}>
              {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              AI Review
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Structured communication for handoffs and escalations
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Context */}
        <div className="p-3 bg-zinc-800/50 rounded-lg">
          <Label className="text-xs text-muted-foreground">Communication Context</Label>
          <Input
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g., Calling physician about patient concern, Shift handoff, Code response"
            className="mt-1"
            data-testid="sbar-context"
          />
        </div>

        {/* SBAR Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SBAR_SECTIONS.map((section) => (
            <div 
              key={section.key} 
              className={`p-4 rounded-lg border ${section.color}`}
            >
              <Label className="font-bold text-lg flex items-center gap-2">
                <section.icon className="w-5 h-5" />
                {section.label}
              </Label>
              
              {/* Prompts */}
              <div className="flex flex-wrap gap-1 mt-2 mb-3">
                {section.prompts.map((prompt, idx) => (
                  <Badge 
                    key={idx}
                    variant="outline" 
                    className="text-xs cursor-pointer hover:bg-white/10"
                    onClick={() => {
                      const current = sections[section.key];
                      updateSection(section.key, current ? `${current}\n${prompt} ` : `${prompt} `);
                    }}
                  >
                    {prompt}
                  </Badge>
                ))}
              </div>
              
              <Textarea
                value={sections[section.key]}
                onChange={(e) => updateSection(section.key, e.target.value)}
                placeholder={section.placeholder}
                className="bg-transparent"
                rows={4}
                data-testid={`sbar-${section.key}`}
              />
            </div>
          ))}
        </div>

        {/* Quick Reference */}
        <div className="p-3 bg-zinc-800/30 rounded-lg text-xs text-muted-foreground">
          <p className="font-medium mb-1">SBAR Tips:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li><strong>Situation:</strong> 10 seconds - What&apos;s happening now</li>
            <li><strong>Background:</strong> 30 seconds - Relevant context only</li>
            <li><strong>Assessment:</strong> State your clinical judgment clearly</li>
            <li><strong>Recommendation:</strong> Be specific about what you need</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default SBARTemplate;
